/**
 * Hull Moving Average (HMA) Calculator
 *
 * The Hull Moving Average is designed to reduce lag while maintaining smoothness.
 * Formula:
 *   1. WMA1 = WMA(period) of source
 *   2. WMA2 = WMA(period/2) of source
 *   3. MMA = 2 * WMA2 - WMA1 (intermediate value)
 *   4. HMA = WMA(sqrt(period)) of MMA
 *
 * Reference: ChartIQ calculateMovingAverageHull implementation
 */

import { slidingWindow, path as pathAccessor } from "../utils";
import { HMA as defaultOptions } from "./defaultOptionsForComputation";

/**
 * Calculate WMA for a given period on raw values
 */
function calculateWMA(values, period) {
  if (values.length < period) return undefined;

  const weight = (period * (period + 1)) / 2;
  let total = 0;

  for (let i = 0; i < period; i++) {
    const value = values[values.length - period + i];
    if (value === undefined || value === null) return undefined;
    total += (i + 1) * value;
  }

  return total / weight;
}

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { windowSize, sourcePath } = options;

    const accessor = pathAccessor(sourcePath);
    const halfPeriod = Math.ceil(windowSize / 2);
    const sqrtPeriod = Math.floor(Math.sqrt(windowSize));

    // We need enough data for full period + sqrt period
    const undefinedLength = windowSize + sqrtPeriod - 2;

    // Step 1 & 2: Calculate WMA(full) and WMA(half) for each point
    const mmaValues = [];

    for (let i = 0; i < data.length; i++) {
      if (i < windowSize - 1) {
        mmaValues.push(undefined);
        continue;
      }

      // Get values for WMA calculations
      const fullPeriodValues = [];
      const halfPeriodValues = [];

      for (let j = i - windowSize + 1; j <= i; j++) {
        const val = accessor(data[j]);
        fullPeriodValues.push(val);
      }

      for (let j = i - halfPeriod + 1; j <= i; j++) {
        const val = accessor(data[j]);
        halfPeriodValues.push(val);
      }

      const wma1 = calculateWMA(fullPeriodValues, windowSize);
      const wma2 = calculateWMA(halfPeriodValues, halfPeriod);

      if (wma1 !== undefined && wma2 !== undefined) {
        // MMA = 2 * WMA(half) - WMA(full)
        mmaValues.push(2 * wma2 - wma1);
      } else {
        mmaValues.push(undefined);
      }
    }

    // Step 3: Calculate WMA(sqrt) on MMA values
    const result = [];

    for (let i = 0; i < data.length; i++) {
      if (i < undefinedLength) {
        result.push(undefined);
        continue;
      }

      // Get MMA values for sqrt period
      const sqrtPeriodValues = [];
      for (let j = i - sqrtPeriod + 1; j <= i; j++) {
        sqrtPeriodValues.push(mmaValues[j]);
      }

      const hma = calculateWMA(sqrtPeriodValues, sqrtPeriod);
      result.push(hma);
    }

    return result;
  }

  calculator.undefinedLength = function () {
    const { windowSize } = options;
    const sqrtPeriod = Math.floor(Math.sqrt(windowSize));
    return windowSize + sqrtPeriod - 2;
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
